import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  Dialog,
} from "@mui/material";

import MailOutlineIcon from "@mui/icons-material/MailOutline";

import SettingsIcon from "@mui/icons-material/Settings";

import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import SyncIcon from "@mui/icons-material/Sync";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import ToolbarIconButton from "./ToolbarIconButton";
import SwipeableThreadRow from "./SwipeableThreadRow";
import { parseThreadKey, getEmailContact } from "./threadGrouping";

import axios from "axios";
import { useUniboxColors } from "../../useUniboxColors";

const API = process.env.REACT_APP_API_URL;

/* ================================================
   HELPERS
================================================ */
function getDateBlock(dateStr) {
  if (!dateStr) return "OTROS";
  const date = new Date(dateStr);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const emailDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const diff = (today - emailDay) / 86400000;

  if (diff === 0) return "HOY";
  if (diff === 1) return "AYER";
  if (diff < 7) return "ESTA SEMANA";

  const monthName = date.toLocaleString("es-ES", { month: "long" });
  if (date.getFullYear() === now.getFullYear()) return monthName;

  return `${monthName} ${date.getFullYear()}`;
}

function getBlockSortTime(people) {
  return people.reduce((max, person) => {
    const t = new Date(person.last?.date || 0).getTime();
    return Number.isFinite(t) ? Math.max(max, t) : max;
  }, 0);
}

function sortGroupedPeopleBlocks(result) {
  return Object.fromEntries(
    Object.entries(result)
      .map(([block, people]) => {
        const sortedPeople = [...people].sort(
          (a, b) => new Date(b.last?.date || 0) - new Date(a.last?.date || 0)
        );
        return [block, sortedPeople, getBlockSortTime(sortedPeople)];
      })
      .sort(([, , timeA], [, , timeB]) => {
        const byDate = timeB - timeA;
        if (byDate !== 0) return byDate;
        return 0;
      })
      .map(([block, people]) => [block, people])
  );
}

function accountColor(email) {
  if (!email) return "#999999";
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "000000".substring(0, 6 - c.length) + c;
}

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "000000".substring(0, 6 - c.length) + c;
}

function extractName(fromStr) {
  if (!fromStr) return null;

  // Si hay un nombre entre comillas o antes del <
  const match = fromStr.match(/^(.*?)(<|$)/);
  if (match) {
    const name = match[1].trim().replace(/(^")|("$)/g, "");
    if (name && !name.includes("@")) return name;
  }

  // Si no hay nombre, devolvemos el email tal cual
  const emailMatch = fromStr.match(/<(.*?)>/);
  return emailMatch ? emailMatch[1] : fromStr;
}

/* ================================================
   COMPONENTE
================================================ */
export default function EmailPanelIzquierdo({
  threads,
  selectedPerson,
  setSelectedPerson,
  search,
  setSearch,
  filter,
  setFilter,
  folder,
  setFolder,
  setOpenAccounts,
  setShowSendPanel, // ADD
  accounts, // ADD
  onEmptyTrash,
  onDeleteConversations,
  isMobile = false,
  listGrouping = "conversation",
  setListGrouping,
  onOpenAttachmentsLibrary,
}) {
  const c = useUniboxColors();
  const [anchorMenu, setAnchorMenu] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [selectedThreadKeys, setSelectedThreadKeys] = useState([]);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [openSwipeKey, setOpenSwipeKey] = useState(null);
  const [showDeleteThreadsDialog, setShowDeleteThreadsDialog] = useState(false);
  const [deleteThreadsBusy, setDeleteThreadsBusy] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const handleOpenMenu = (e) => setAnchorMenu(e.currentTarget);
  const handleCloseMenu = () => setAnchorMenu(null);
  const [emptyTrashBusy, setEmptyTrashBusy] = useState(false);
  const filterTab = folder === "trash" ? "trash" : filter;

  useEffect(() => {
    setSelectedThreadKeys([]);
    setBulkSelectMode(false);
    setOpenSwipeKey(null);
  }, [folder, filter, search, listGrouping]);

  /* ================= GROUPING ================= */
  const groupedPeople = useMemo(() => {
    const result = {};
    const s = search.toLowerCase();

    for (const threadKey in threads) {
      let mails = threads[threadKey];
      if (!mails.length) continue;

      const parsed = parseThreadKey(threadKey);

      // FILTRO DE BUSQUEDA
      if (s) {
        const matchesEmails = mails.some(
          (e) =>
            e.subject?.toLowerCase().includes(s) ||
            e.snippet?.toLowerCase().includes(s) ||
            e.from?.toLowerCase().includes(s) ||
            e.to_email?.toLowerCase().includes(s)
        );
        if (!matchesEmails) {
          const fallbackContact =
            parsed.contact || getEmailContact(mails[0]) || threadKey;
          const fallbackName =
            listGrouping === "conversation"
              ? mails[0]?.subject || ""
              : extractName(fallbackContact) || fallbackContact;
          if (!fallbackName.toLowerCase().includes(s)) continue;
        }
      }

      if (filter === "read") mails = mails.filter((e) => e.isRead);
      if (filter === "unread") mails = mails.filter((e) => !e.isRead);
      if (!mails.length) continue;

      const sorted = [...mails].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      const last = sorted[0];
      const contactEmail =
        parsed.contact || getEmailContact(last) || threadKey;
      const displayName =
        listGrouping === "conversation"
          ? last.subject?.trim() || "(Sin asunto)"
          : extractName(contactEmail) || contactEmail;
      const displaySecondary =
        listGrouping === "conversation"
          ? extractName(contactEmail) || contactEmail
          : last.snippet
            ? last.snippet.slice(0, 55) + "…"
            : last.subject;
      const block = getDateBlock(last.date);
      const unread = mails.filter((x) => !x.isRead).length;
      const distinctAccounts = new Set(
        mails.map((m) => (m.accountEmail || "").toLowerCase()).filter(Boolean)
      );
      const accountList = Array.from(distinctAccounts).sort();
      const threadAccount = parsed.account;
      const accountLabel =
        threadAccount ||
        (accountList.length <= 1
          ? accountList[0] || ""
          : `${accountList.length} cuentas`);
      const accountTooltip = threadAccount
        ? `Cuenta utilizada en este hilo: ${threadAccount}`
        : accountList.length === 0
          ? ""
          : accountList.length === 1
            ? `Cuenta utilizada en este hilo: ${accountList[0]}`
            : `Este hilo mezcla mensajes desde ${accountList.length} cuentas conectadas en UNIBOX: ${accountList.join(" · ")}`;

      if (!result[block]) result[block] = [];

      result[block].push({
        key: threadKey,
        name: displayName,
        secondary: displaySecondary,
        avatarLabel: extractName(contactEmail) || contactEmail,
        accountEmail: accountLabel,
        accountTooltip,
        unread,
        last,
        mailCount: mails.length,
      });
    }

    return sortGroupedPeopleBlocks(result);
  }, [threads, search, filter, listGrouping]);

  const visibleThreadKeys = useMemo(
    () =>
      Object.values(groupedPeople).flatMap((people) =>
        people.map((p) => p.key)
      ),
    [groupedPeople]
  );

  const allThreadsSelected =
    visibleThreadKeys.length > 0 &&
    visibleThreadKeys.every((key) => selectedThreadKeys.includes(key));

  const selectedMailCount = useMemo(
    () =>
      selectedThreadKeys.reduce(
        (sum, key) => sum + (threads[key]?.length || 0),
        0
      ),
    [selectedThreadKeys, threads]
  );

  const toggleThreadSelection = (key, event) => {
    event?.stopPropagation?.();
    setSelectedThreadKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleSelectAllThreads = () => {
    if (allThreadsSelected) {
      setSelectedThreadKeys([]);
      return;
    }
    setSelectedThreadKeys(visibleThreadKeys);
  };

  const clearThreadSelection = () => {
    setSelectedThreadKeys([]);
    setBulkSelectMode(false);
    setOpenSwipeKey(null);
  };

  const requestDeleteThreads = (keys) => {
    setSelectedThreadKeys(keys);
    setShowDeleteThreadsDialog(true);
    setOpenSwipeKey(null);
  };

  const confirmDeleteSelectedThreads = async () => {
    if (!selectedThreadKeys.length || deleteThreadsBusy) return;
    try {
      setDeleteThreadsBusy(true);
      await onDeleteConversations?.(selectedThreadKeys);
      setSelectedThreadKeys([]);
      setBulkSelectMode(false);
      setShowDeleteThreadsDialog(false);
      setToast({
        open: true,
        severity: "success",
        message:
          folder === "trash"
            ? "Conversaciones eliminadas definitivamente."
            : "Conversaciones movidas a la papelera.",
      });
    } catch (err) {
      console.error("Error eliminando conversaciones:", err);
      setToast({
        open: true,
        severity: "error",
        message: "No se pudieron eliminar las conversaciones.",
      });
    } finally {
      setDeleteThreadsBusy(false);
    }
  };

  const menuBtnSx = {
    fontSize: 11,
    display: "flex",
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 600,
    color: c.textPrimary,
    borderColor: c.border,
    borderRadius: c.radius,
    py: 0.85,
    "&:hover": {
      bgcolor: c.cardHover,
      borderColor: c.accentBorder,
      color: c.accent,
    },
    "& .MuiButton-startIcon": {
      color: "inherit",
    },
  };

  const menuBtnDangerSx = {
    ...menuBtnSx,
    color: c.isDark ? "#fca5a5" : "#b91c1c",
    borderColor: c.isDark ? "rgba(248, 113, 113, 0.55)" : "#b91c1c",
    "&:hover": {
      bgcolor: c.isDark ? "rgba(248, 113, 113, 0.12)" : "#fef2f2",
      borderColor: c.isDark ? "#f87171" : "#b91c1c",
      color: c.isDark ? "#fecaca" : "#991b1b",
    },
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 340 },
        flexShrink: 0,
        borderRight: { md: `1px solid ${c.border}` },
        display: "flex",
        flexDirection: "column",
        bgcolor: c.panel,
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 2.2 }}>
        {/* TITLE */}

        <Box
          sx={{
            pt: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            /*  gap: 1, */
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 22 },
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: c.textPrimary,
            }}
          >
            MATRIXBOX
          </Typography>
          <Box
            sx={{
              /* pt: 2, */
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            <Tooltip title="Opciones">
              <IconButton
                size="small"
                onClick={handleOpenMenu}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <SettingsIcon sx={{ fontSize: "1.5rem", color: c.textPrimary }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="New Email">
              <IconButton
                size="small"
                onClick={() => setShowSendPanel(true)}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <MarkEmailReadIcon
                  sx={{ fontSize: "1.5rem", color: "#00bbcd" }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* MENU DESPLEGABLE */}
        <Menu
          anchorEl={anchorMenu}
          open={Boolean(anchorMenu)}
          onClose={handleCloseMenu}
          slotProps={{
            paper: {
              sx: {
                bgcolor: c.card,
                border: `1px solid ${c.border}`,
                borderRadius: c.radiusOuter,
                boxShadow: c.shadowMd,
                minWidth: 188,
                mt: 0.5,
              },
            },
          }}
          MenuListProps={{ sx: { py: 0.75, px: 0.5 } }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              setOpenAccounts(true);
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={menuBtnSx}
              variant="outlined"
              fullWidth
              startIcon={<AccountCircleOutlinedIcon />}
            >
              Cuentas
            </Button>
          </MenuItem>

          <MenuItem
            onClick={async () => {
              if (syncBusy) return;
              setSyncBusy(true);
              const failedAccounts = [];

              for (const acc of accounts) {
                try {
                  await axios.get(`${API}/gmail/sync/${acc.email}`);
                } catch (err) {
                  const code = err?.response?.data?.code;
                  if (code === "GOOGLE_REAUTH_REQUIRED") {
                    failedAccounts.push(acc.email);
                    continue;
                  }
                  failedAccounts.push(`${acc.email} (error tecnico)`);
                }
              }

              if (failedAccounts.length > 0) {
                setToast({
                  open: true,
                  severity: "warning",
                  message: `Sync parcial. Revisar: ${failedAccounts.join(", ")}`,
                });
                setSyncBusy(false);
                return;
              }

              setToast({
                open: true,
                severity: "success",
                message: "Sync completado en todas las cuentas.",
              });
              setSyncBusy(false);
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={menuBtnSx}
              variant="outlined"
              fullWidth
              startIcon={
                syncBusy ? <CircularProgress size={14} /> : <SyncIcon />
              }
              disabled={syncBusy}
            >
              {syncBusy ? "Sincronizando..." : "Sync"}
            </Button>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleCloseMenu();
              onOpenAttachmentsLibrary?.();
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={menuBtnSx}
              variant="outlined"
              fullWidth
              startIcon={<AttachFileOutlinedIcon />}
            >
              Archivos adjuntos
            </Button>
          </MenuItem>

          <Divider sx={{ my: 0.5, borderColor: c.border }} />

          <MenuItem
            onClick={() => {
              setListGrouping?.("account");
              handleCloseMenu();
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={{
                ...menuBtnSx,
                ...(listGrouping === "account"
                  ? {
                      bgcolor: "#00bbcd",
                      color: "#fff",
                      borderColor: "#00bbcd",
                      "&:hover": {
                        bgcolor: "#00a5b5",
                        borderColor: "#00a5b5",
                        color: "#fff",
                      },
                    }
                  : {}),
              }}
              variant={listGrouping === "account" ? "contained" : "outlined"}
              fullWidth
              startIcon={<SwitchAccountOutlinedIcon />}
              endIcon={
                listGrouping === "account" ? (
                  <CheckIcon sx={{ fontSize: "1rem !important" }} />
                ) : null
              }
            >
              Por cuenta
            </Button>
          </MenuItem>
          <Typography
            sx={{
              px: 1.25,
              pb: 0.35,
              fontSize: 9.5,
              color: c.textDim,
              lineHeight: 1.3,
            }}
          >
            Todos los mensajes con el mismo contacto
          </Typography>

          <MenuItem
            onClick={() => {
              setListGrouping?.("conversation");
              handleCloseMenu();
            }}
            sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
          >
            <Button
              sx={{
                ...menuBtnSx,
                ...(listGrouping === "conversation"
                  ? {
                      bgcolor: "#00bbcd",
                      color: "#fff",
                      borderColor: "#00bbcd",
                      "&:hover": {
                        bgcolor: "#00a5b5",
                        borderColor: "#00a5b5",
                        color: "#fff",
                      },
                    }
                  : {}),
              }}
              variant={listGrouping === "conversation" ? "contained" : "outlined"}
              fullWidth
              startIcon={<ForumOutlinedIcon />}
              endIcon={
                listGrouping === "conversation" ? (
                  <CheckIcon sx={{ fontSize: "1rem !important" }} />
                ) : null
              }
            >
              Por conversación
            </Button>
          </MenuItem>
          <Typography
            sx={{
              px: 1.25,
              pb: 0.35,
              fontSize: 9.5,
              color: c.textDim,
              lineHeight: 1.3,
            }}
          >
            Un hilo por asunto (Re:)
          </Typography>

          {folder === "trash" && (
            <MenuItem
              onClick={async () => {
                if (emptyTrashBusy) return;
                const confirmed = window.confirm(
                  "Vas a eliminar definitivamente todos los emails de la papelera. Esta accion no se puede deshacer. Continuar?"
                );
                if (!confirmed) return;

                try {
                  setEmptyTrashBusy(true);
                  await onEmptyTrash?.();
                  setToast({
                    open: true,
                    severity: "success",
                    message: "Papelera vaciada correctamente.",
                  });
                } catch (err) {
                  console.error("Error vaciando papelera:", err);
                  setToast({
                    open: true,
                    severity: "error",
                    message: "No se pudo vaciar la papelera.",
                  });
                } finally {
                  setEmptyTrashBusy(false);
                  handleCloseMenu();
                }
              }}
              sx={{ py: 0.4, px: 0.75, borderRadius: c.radius, "&:hover": { bgcolor: "transparent" } }}
            >
              <Button
                sx={menuBtnDangerSx}
                variant="outlined"
                fullWidth
                startIcon={
                  emptyTrashBusy ? <CircularProgress size={14} /> : <DeleteIcon />
                }
                disabled={emptyTrashBusy}
              >
                {emptyTrashBusy ? "Vaciando..." : "Vaciar papelera"}
              </Button>
            </MenuItem>
          )}
        </Menu>

        {/* 🟦 BOTONES NUEVO / SYNC / CUENTAS — REINSERTADOS */}

        {/* BUSCADOR */}
        <TextField
          fullWidth
          placeholder="Buscar…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: c.radiusOuter,
              backgroundColor: c.card,
              boxShadow: c.shadowSm,
              "& fieldset": { borderColor: c.border },
            },
          }}
        />

        {/* FILTROS */}
        <ToggleButtonGroup
          value={filterTab}
          exclusive
          onChange={(e, v) => {
            if (!v) return;
            if (v === "trash") {
              setFolder("trash");
              return;
            }
            setFolder("inbox");
            setFilter(v);
          }}
          sx={{
            mt: 1.25,
            width: "100%",
            bgcolor: c.card,
            borderRadius: c.radiusOuter,
            p: 0.35,
            boxShadow: c.shadowSm,
            border: `1px solid ${c.border}`,
            "& .MuiToggleButton-root": {
              py: "6px !important",
              border: 0,
              borderRadius: `${c.radius} !important`,
              mx: 0.15,
              fontSize: 10,
              fontWeight: 700,
              color: c.textMuted,
              "&.Mui-selected": {
                bgcolor: c.filterSelectedBg,
                color: c.filterSelectedColor,
                fontWeight: 800,
                boxShadow: `inset 0 0 0 1px ${c.selectedBorder}`,
              },
            },
          }}
        >
          <ToggleButton
            value="all"
            sx={{ fontSize: 9, flex: 1, fontWeight: 700 }}
          >
            Todos
          </ToggleButton>
          {folder !== "trash" && (
            <ToggleButton
              value="unread"
              sx={{ fontSize: 9, flex: 1, fontWeight: 700 }}
            >
              No leídos
            </ToggleButton>
          )}
          {folder !== "trash" && (
            <ToggleButton
              value="read"
              sx={{ fontSize: 9, flex: 1, fontWeight: 700 }}
            >
              Leídos
            </ToggleButton>
          )}
          <ToggleButton
            value="trash"
            color="error"
            sx={{ fontSize: 9, flex: 1, fontWeight: 700 }}
          >
            Papelera
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ mt: 0.85, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={() => {
              if (bulkSelectMode) {
                clearThreadSelection();
                return;
              }
              setBulkSelectMode(true);
              setOpenSwipeKey(null);
            }}
            sx={{
              textTransform: "none",
              fontSize: 12,
              fontWeight: 700,
              color: bulkSelectMode ? c.textPrimary : c.accent,
              minWidth: 0,
              px: 0.5,
            }}
          >
            {bulkSelectMode ? "Listo" : "Seleccionar"}
          </Button>
        </Box>

        {bulkSelectMode && selectedThreadKeys.length > 0 && (
          <Box
            sx={{
              mt: 1.25,
              px: 1.15,
              py: 0.9,
              borderRadius: c.radiusOuter,
              bgcolor: c.accentSoft,
              border: `1px solid ${c.accentBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: c.textPrimary,
                  lineHeight: 1.25,
                }}
              >
                {selectedThreadKeys.length}{" "}
                {selectedThreadKeys.length === 1
                  ? "conversación"
                  : "conversaciones"}
              </Typography>
              <Typography
                sx={{ fontSize: 10, color: c.textMuted, mt: 0.2, lineHeight: 1.2 }}
              >
                {selectedMailCount} mensaje
                {selectedMailCount === 1 ? "" : "s"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.45,
                flexShrink: 0,
              }}
            >
              {visibleThreadKeys.length > 1 && (
                <ToolbarIconButton
                  title={allThreadsSelected ? "Quitar todos" : "Seleccionar todos"}
                  onClick={toggleSelectAllThreads}
                  active={allThreadsSelected}
                  size={32}
                  icon={<SelectAllIcon sx={{ fontSize: "0.95rem" }} />}
                />
              )}
              <ToolbarIconButton
                title={
                  folder === "trash"
                    ? "Eliminar definitivamente"
                    : "Mover a papelera"
                }
                onClick={() => setShowDeleteThreadsDialog(true)}
                variant="danger"
                size={32}
                icon={<DeleteIcon sx={{ fontSize: "0.95rem" }} />}
              />
              <ToolbarIconButton
                title="Cancelar selección"
                onClick={clearThreadSelection}
                size={32}
                icon={<CloseIcon sx={{ fontSize: "0.95rem" }} />}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* LISTA DE PERSONAS */}
      <Box
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
        onClick={() => setOpenSwipeKey(null)}
      >
        {Object.entries(groupedPeople).map(([block, people], blockIndex) => (
          <React.Fragment key={block}>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                position: "sticky",
                top: 0,
                zIndex: blockIndex + 1,
                bgcolor: c.panel,
                borderBottom: `1px solid ${c.border}`,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 10,
                  color: c.textDim,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                {block}
              </Typography>
            </Box>
           
            <List disablePadding>
              {people.map(
                ({
                  key,
                  name,
                  secondary,
                  avatarLabel,
                  accountEmail,
                  accountTooltip,
                  unread,
                  last,
                }) => {
                  const threadSelected = selectedThreadKeys.includes(key);

                  const row = (
                    <ListItemButton
                      selected={selectedPerson === key && !threadSelected}
                      onClick={() => {
                        setOpenSwipeKey(null);
                        setSelectedPerson(key);
                      }}
                      sx={{
                        py: 1.25,
                        pl: bulkSelectMode ? 0.75 : 1.5,
                        pr: 2,
                        mx: 0,
                        mb: 0,
                        width: "100%",
                        borderRadius: c.radius,
                        border: "1px solid",
                        borderColor: "transparent",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.25,
                        bgcolor: threadSelected ? c.accentSoft : c.card,
                        boxShadow: c.shadowSm,
                        "&.Mui-selected": {
                          backgroundColor: c.listSelected,
                          borderColor: c.selectedBorder,
                          boxShadow: c.shadowActive,
                        },
                        "&:hover": {
                          backgroundColor: threadSelected ? c.selected : c.cardHover,
                        },
                      }}
                    >
                      {bulkSelectMode && (
                        <Checkbox
                          size="small"
                          checked={threadSelected}
                          onClick={(e) => toggleThreadSelection(key, e)}
                          sx={{ p: 0.5, mt: 0.5, flexShrink: 0 }}
                        />
                      )}

                      <Box sx={{ position: "relative", mr: 0.5, flexShrink: 0 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: -5,
                        left: 0,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: accountColor(last.accountEmail),
                        border: `2px solid ${c.card}`,
                        zIndex: 5,
                      }}
                    />
                    <Avatar
                      sx={{
                        bgcolor: stringToColor(avatarLabel || name),
                        width: 38,
                        height: 38,
                      }}
                    >
                      {(avatarLabel || name).charAt(0).toUpperCase()}
                    </Avatar>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ListItemText
                      sx={{
                        m: 0,
                        "& .MuiListItemText-primary": {
                          fontWeight: 800,
                          fontSize: 13,
                          textOverflow: "ellipsis",
                          display: "block",
                          mr: 3.7,
                        },
                        "& .MuiListItemText-secondary": {
                          fontSize: 11.5,
                          color: c.textMuted,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          mr: 3.5,
                        },
                      }}
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 800,
                              color: c.textPrimary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {name}
                          </Typography>
                        </Box>
                      }
                      secondary={secondary}
                    />
                    {accountEmail ? (
                      <Tooltip
                        title={accountTooltip || accountEmail}
                        placement="top"
                        enterDelay={400}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 10,
                            color: c.textMuted,
                            border: `1px solid ${c.border}`,
                            borderRadius: c.radius,
                            px: 0.8,
                            py: 0.1,
                            mt: 0.5,
                            bgcolor: c.metaBg,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "inline-block",
                            cursor: "default",
                          }}
                        >
                          {accountEmail}
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </Box>

                  {unread > 0 ? (
                    <Badge
                      badgeContent={unread}
                      sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        "& .MuiBadge-badge": {
                          backgroundColor: "#00bbcd",
                          color: "#fff",
                          fontWeight: 700,
                        },
                      }}
                    />
                  ) : null}
                    </ListItemButton>
                  );

                  return (
                    <Box key={key} sx={{ mx: 1, mb: 0.5 }}>
                      {bulkSelectMode ? (
                        row
                      ) : (
                        <SwipeableThreadRow
                          enabled={!bulkSelectMode}
                          isOpen={openSwipeKey === key}
                          onOpen={() => setOpenSwipeKey(key)}
                          onClose={() =>
                            setOpenSwipeKey((prev) => (prev === key ? null : prev))
                          }
                          onDelete={() => requestDeleteThreads([key])}
                        >
                          {row}
                        </SwipeableThreadRow>
                      )}
                    </Box>
                  );
                }
              )}
            </List>
          </React.Fragment>
        ))}
      </Box>

      <Dialog
        open={showDeleteThreadsDialog}
        onClose={() => !deleteThreadsBusy && setShowDeleteThreadsDialog(false)}
      >
        <Box sx={{ p: 3, maxWidth: 420 }}>
          <Typography variant="h6">¿Eliminar conversaciones seleccionadas?</Typography>
          <Typography sx={{ color: c.textMuted, mt: 1, fontSize: 14 }}>
            {folder === "trash"
              ? `Se eliminarán definitivamente ${selectedMailCount} mensaje(s) de ${selectedThreadKeys.length} conversación(es).`
              : `Se moverán a la papelera ${selectedMailCount} mensaje(s) de ${selectedThreadKeys.length} conversación(es).`}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button
              onClick={() => setShowDeleteThreadsDialog(false)}
              disabled={deleteThreadsBusy}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDeleteSelectedThreads}
              disabled={deleteThreadsBusy}
              startIcon={
                deleteThreadsBusy ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {deleteThreadsBusy ? "Eliminando..." : "Eliminar"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
